// AGGREGATE, ported from internal/wire/aggregate.go.
//
// The zoomed-out tier of SPEC §3: per-chunk majority owner and contested-ness,
// recomputed every AggregateEveryTick (~2 s). A client that is zoomed out takes
// this instead of subscribing to per-pixel deltas.
//
//	u16 count
//	count x (u16 chunkId | u16 ownerCountryId | u16 ownerPixels | u8 contested)
//
// Seven bytes per chunk over ~100 chunks is ~700 B every 2 s.
//
// Parallel typed arrays again, and for a second reason here: this is what the
// world-zoom renderer uploads as a 10x10 texture, so an array of objects would
// have to be flattened before it could be drawn anyway.

import { Reader, Writer } from './bytes';
import { ClassAggregate } from './classes';
import { NumChunks, PixelsPerChunk, validChunk, type TickNo } from './core';
import { errChunkID, errPixelCount, errTooManyRecords, errTruncated } from './errors';

/** Seven bytes. */
export const ChunkAggregateSize = 2 + 2 + 2 + 1;

export interface Aggregates {
  tick: TickNo;
  count: number;
  chunk: Uint16Array;
  /** The country holding the most pixels in the chunk, ignoring neutral. Ties break to the lowest id. */
  owner: Uint16Array;
  /** How many pixels `owner` holds — what lets the client fade a chunk's fill by how much of it is claimed. */
  ownerPixels: Uint16Array;
  /** 0 when one country holds every owned pixel, rising as ownership fragments. */
  contested: Uint8Array;
}

export function decodeAggregates(b: Uint8Array): Aggregates {
  const r = new Reader(b);
  const tick = r.header(ClassAggregate);
  const n = r.u16();
  if (n > NumChunks) throw errTooManyRecords();
  if (n * ChunkAggregateSize > r.remaining) throw errTruncated();

  const chunk = new Uint16Array(n);
  const owner = new Uint16Array(n);
  const ownerPixels = new Uint16Array(n);
  const contested = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const c = r.u16();
    owner[i] = r.u16();
    const op = r.u16();
    contested[i] = r.u8();
    if (!validChunk(c)) throw errChunkID();
    if (op > PixelsPerChunk) throw errPixelCount();
    chunk[i] = c;
    ownerPixels[i] = op;
  }
  r.done();
  return { tick, count: n, chunk, owner, ownerPixels, contested };
}

export interface ChunkAggregate {
  chunk: number;
  owner: number;
  ownerPixels: number;
  contested: number;
}

/**
 * The encoder is server-side, and exists here only so a test can build a frame
 * the decoder is then run against without reaching for a fixture file.
 */
export function encodeAggregates(tick: TickNo, aggs: readonly ChunkAggregate[]): Uint8Array {
  if (aggs.length > NumChunks) throw errTooManyRecords();
  const w = new Writer(9 + 2 + aggs.length * ChunkAggregateSize);
  w.header(ClassAggregate, tick).u16(aggs.length);
  for (const a of aggs) {
    if (!validChunk(a.chunk)) throw errChunkID();
    if (a.ownerPixels > PixelsPerChunk) throw errPixelCount();
    w.u16(a.chunk).u16(a.owner).u16(a.ownerPixels).u8(a.contested);
  }
  return w.finish();
}
